import React, { Component } from 'react'
import gql from 'graphql-tag'
import { Alert, Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Progress, Row } from 'reactstrap'
import Select from 'react-select'

import { RepsTypeAhead } from '../TaskManagement/TaskComponents/TypeAheadComponents'
import withApolloClient from '../withApolloClient'

const StatusOptionsQuery = gql`
query BulkIncidentStatusOptions {
    optionsList: __type(name: "IncidentsIncidentStatusChoices") {
        states: enumValues {
            name
            description
        }
    }
}`

const BulkIncidentActionMutation = gql`
mutation BulkIncidentAction($input: BulkIncidentActionMutationInput!) {
    bulkIncidentAction(input: $input) {
        ok
        errors {
            messages
        }
    }
}`

const ACTION_CONFIG = {
    ASSIGN_REP: {
        title: "Assign Reps",
        button: "Assign Reps",
        color: "primary",
    },
    UNASSIGN_REP: {
        title: "Unassign Reps",
        button: "Unassign Reps",
        color: "danger",
    },
    CHANGE_STATUS: {
        title: "Change Status",
        button: "Change Status",
        color: "primary",
    },
}

class BulkIncidentActionModal extends Component {
    constructor(props) {
        super(props)
        this.state = {
            value: null,
            statusOptions: [],
            loadingStatuses: false,
            loading: false,
            error: null,
        }
    }

    componentDidMount() {
        if (this.props.actionType === "CHANGE_STATUS") {
            this.loadStatusOptions()
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.actionType !== this.props.actionType || prevProps.open !== this.props.open) {
            this.setState({ value: null, loading: false, error: null })
            if (this.props.actionType === "CHANGE_STATUS" && !this.state.statusOptions.length) {
                this.loadStatusOptions()
            }
        }
    }

    loadStatusOptions = () => {
        this.setState({ loadingStatuses: true })
        this.props.apolloClient.query({
            query: StatusOptionsQuery,
            fetchPolicy: 'network-only',
        }).then((result) => {
            const statusOptions = result?.data?.optionsList?.states?.map(status => ({
                value: status.name,
                label: status.description,
            })) || []
            this.setState({ statusOptions, loadingStatuses: false })
        }).catch(() => {
            this.setState({ loadingStatuses: false, error: 'Could not load incident statuses.' })
        })
    }

    handleClose = () => {
        this.setState({ value: null, loading: false, error: null })
        this.props.handleClose()
    }

    getMutationValue = () => {
        if (this.props.actionType === "ASSIGN_REP") {
            return Array.isArray(this.state.value) ? this.state.value : [this.state.value].filter(Boolean)
        }
        return this.state.value
    }

    validate = (value) => {
        if (!this.props.incidentIds?.length) {
            return 'Please select at least one incident.'
        }
        if (this.props.actionType === "ASSIGN_REP" && !value.length) {
            return 'Please select at least one representative.'
        }
        if (this.props.actionType === "CHANGE_STATUS" && !value) {
            return 'Please select a status.'
        }
        return null
    }

    submit = () => {
        const value = this.getMutationValue()
        const error = this.validate(value)
        if (error) {
            this.setState({ error })
            return
        }

        const input = {
            incidentIds: this.props.incidentIds,
            actionType: this.props.actionType,
        }
        if (this.props.actionType !== "UNASSIGN_REP") {
            input.value = value
        }

        this.setState({ loading: true, error: null })
        this.props.apolloClient.mutate({
            mutation: BulkIncidentActionMutation,
            variables: { input },
        }).then((result) => {
            const response = result?.data?.bulkIncidentAction
            if (response?.ok) {
                this.props.refetchQuery && this.props.refetchQuery()
                this.handleClose()
                return
            }

            const error = response?.errors?.[0]?.messages?.toString() || 'Could not complete bulk action.'
            this.setState({ error, loading: false })
        }).catch((error) => {
            this.setState({ error: error.message || 'An error has occurred.', loading: false })
        })
    }

    renderActionField = () => {
        if (this.props.actionType === "ASSIGN_REP") {
            return (
                <RepsTypeAhead
                    onChange={(value) => this.setState({ value, error: null })}
                    defaultValue={this.state.value}
                    permissions={["custom_can_assign_to_incident"]}
                />
            )
        }
        if (this.props.actionType === "CHANGE_STATUS") {
            return (
                <Select
                    className="bos-custom-select pb-2"
                    classNamePrefix="bos-select"
                    placeholder="Select Status"
                    isLoading={this.state.loadingStatuses}
                    options={this.state.statusOptions}
                    isClearable
                    onChange={status => this.setState({ value: status ? status.value : null, error: null })}
                />
            )
        }
        return `This will remove all assigned reps from ${this.props.incidentIds?.length || 0} selected incident${this.props.incidentIds?.length === 1 ? "" : "s"}.`
    }

    render() {
        const config = ACTION_CONFIG[this.props.actionType] || ACTION_CONFIG.ASSIGN_REP

        return (
            <Modal isOpen={this.props.open} toggle={this.handleClose}>
                <ModalHeader>{config.title}</ModalHeader>
                <ModalBody>
                    <Row>
                        {this.state.error && <Col xs={12}><Alert color="danger">{this.state.error}</Alert></Col>}
                        <Col xs={12}>{this.renderActionField()}</Col>
                    </Row>
                    {this.state.loading && <Row><Col xs={12}><Progress animated color="info" value="100" /></Col></Row>}
                </ModalBody>
                <ModalFooter>
                    {!this.state.loading && <>
                        <Button outline color="secondary" onClick={this.handleClose}>Close</Button>&nbsp;&nbsp;
                        <Button color={config.color} onClick={this.submit}>{config.button}</Button>
                    </>}
                </ModalFooter>
            </Modal>
        )
    }
}

export default withApolloClient(BulkIncidentActionModal)
